import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import type { Json } from "@/integrations/supabase/types";
import { DEFAULT_WHATSAPP_ENV_CONFIG, type WhatsappEnvConfig } from "@/lib/rentalWhatsapp";

export type SettingsCategory = "general" | "security" | "i18n" | "notifications" | "smtp" | "twilio" | "resend" | "whatsapp_meta" | "print" | "whatsapp_rental";

export type BusinessType = "empresa" | "autonomo";
export type VatRegime = "general" | "recargo_equivalencia";

export interface GeneralSettings {
  companyName: string;
  email: string;
  phone: string;
  website: string;
  address: string;
  city: string;
  province: string;
  country: string;
  postalCode: string;
  taxId: string;
  logoUrl: string;
  facebook: string;
  instagram: string;
  twitter: string;
  linkedin: string;
  darkMode: boolean;
  compactMode: boolean;
  autoSave: boolean;
  googleAnalyticsId: string;
  googleTagManagerId: string;
  googleMyBusinessUrl: string;
  businessType: BusinessType;
  vatRegime: VatRegime;
  invoiceFooterText: string;
  invoiceFooterPV: string;
  invoiceFooterRepair: string;
  nextInvoiceNumber: number;
  resetNumberingByYear: boolean;
  activeSeries: string;
  showQrA4: boolean;
  showQrTicket: boolean;
}

export interface SecuritySettings {
  twoFactorEnabled: boolean;
  sessionTimeout: number;
  loginNotifications: boolean;
  ipWhitelist: boolean;
  rightClickProtection: boolean;
}

export interface I18nSettings {
  language: string;
  region: string;
  timezone: string;
  dateFormat: string;
  timeFormat: string;
  currency: string;
  currencyPosition: string;
  useSystemTimezone: boolean;
}

export interface NotificationSettings {
  inAppEnabled: boolean;
  inAppCRM: boolean;
  inAppOrders: boolean;
  inAppInvoices: boolean;
  inAppSystem: boolean;
  emailEnabled: boolean;
  emailCRM: boolean;
  emailOrders: boolean;
  emailInvoices: boolean;
  emailDigest: boolean;
  pushEnabled: boolean;
  pushUrgent: boolean;
}

export interface SmtpSettings {
  host: string;
  port: number;
  user: string;
  password: string;
  fromEmail: string;
  fromName: string;
  secure: boolean;
}

export interface TwilioSettings {
  accountSid: string;
  authToken: string;
  whatsappFrom: string;
  enabled: boolean;
}

export interface ResendSettings {
  apiKey: string;
  fromEmail: string;
  fromName: string;
  enabled: boolean;
}

export interface WhatsAppMetaSettings {
  appId: string;
  appSecret: string;
  phoneNumberId: string;
  businessAccountId: string;
  accessToken: string;
  verifyToken: string;
  webhookUrl: string;
  enabled: boolean;
}

export interface PrintSettings {
  a4Margin: number;
  a4FontSize: number;
  a4Density: "compact" | "normal" | "relaxed";
  ticketMargin: number;
  ticketFontSize: number;
  ticketDensity: "compact" | "normal" | "relaxed";
}

export type WhatsappRentalSettings = WhatsappEnvConfig;

type SettingsMap = {
  general: GeneralSettings;
  security: SecuritySettings;
  i18n: I18nSettings;
  notifications: NotificationSettings;
  smtp: SmtpSettings;
  twilio: TwilioSettings;
  resend: ResendSettings;
  whatsapp_meta: WhatsAppMetaSettings;
  print: PrintSettings;
  whatsapp_rental: WhatsappRentalSettings;
};

const defaultSettings: SettingsMap = {
  general: {
    companyName: "Mi Empresa S.A.",
    email: "contacto@miempresa.com",
    phone: "+1 234 567 890",
    website: "https://miempresa.com",
    address: "Calle Principal 123",
    city: "Ciudad",
    province: "",
    country: "España",
    postalCode: "",
    taxId: "12345678-9",
    logoUrl: "",
    facebook: "",
    instagram: "",
    twitter: "",
    linkedin: "",
    darkMode: false,
    compactMode: false,
    autoSave: true,
    googleAnalyticsId: "",
    googleTagManagerId: "",
    googleMyBusinessUrl: "",
    businessType: "empresa",
    vatRegime: "general",
    invoiceFooterText: "Garantía de piezas: 2 años. Garantía de mano de obra: 3 meses o 2.000 km (lo que antes suceda).",
    invoiceFooterPV: "Gracias por su compra. Para cualquier devolución es imprescindible presentar este ticket.",
    invoiceFooterRepair: "Garantía de reparación: 3 meses o 2.000 km según normativa vigente. Piezas nuevas: 2 años.",
    nextInvoiceNumber: 1,
    resetNumberingByYear: true,
    activeSeries: "",
    showQrA4: true,
    showQrTicket: true,
  },
  security: {
    twoFactorEnabled: false,
    sessionTimeout: 30,
    loginNotifications: true,
    ipWhitelist: false,
    rightClickProtection: false,
  },
  i18n: {
    language: "es",
    region: "es-ES",
    timezone: "Europe/Madrid",
    dateFormat: "DD/MM/YYYY",
    timeFormat: "24h",
    currency: "EUR",
    currencyPosition: "after",
    useSystemTimezone: true,
  },
  notifications: {
    inAppEnabled: true,
    inAppCRM: true,
    inAppOrders: true,
    inAppInvoices: true,
    inAppSystem: true,
    emailEnabled: true,
    emailCRM: true,
    emailOrders: false,
    emailInvoices: true,
    emailDigest: true,
    pushEnabled: false,
    pushUrgent: true,
  },
  smtp: {
    host: "",
    port: 587,
    user: "",
    password: "",
    fromEmail: "",
    fromName: "",
    secure: false,
  },
  twilio: {
    accountSid: "",
    authToken: "",
    whatsappFrom: "",
    enabled: false,
  },
  resend: {
    apiKey: "",
    fromEmail: "",
    fromName: "",
    enabled: false,
  },
  whatsapp_meta: {
    appId: "",
    appSecret: "",
    phoneNumberId: "",
    businessAccountId: "",
    accessToken: "",
    verifyToken: "",
    webhookUrl: "",
    enabled: false,
  },
  print: {
    a4Margin: 18,
    a4FontSize: 11,
    a4Density: "normal",
    ticketMargin: 4,
    ticketFontSize: 12,
    ticketDensity: "normal",
  },
  whatsapp_rental: DEFAULT_WHATSAPP_ENV_CONFIG,
};

export function useSettings<T extends SettingsCategory>(category: T) {
  const { user } = useAuth();
  const [settings, setSettings] = useState<SettingsMap[T]>(defaultSettings[category]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Use user.id to prevent unnecessary re-fetches on window focus
  const userId = user?.id;

  const fetchSettings = useCallback(async (isInitialLoad = false) => {
    if (!userId) {
      setLoading(false);
      return;
    }

    // Only set loading on initial fetch to avoid UI flickers on background refreshes if any
    if (isInitialLoad) setLoading(true);

    try {
      const { data, error } = await supabase
        .from("settings")
        .select("settings")
        .eq("user_id", userId)
        .eq("category", category)
        .maybeSingle();

      if (error) throw error;

      if (data?.settings && typeof data.settings === "object" && !Array.isArray(data.settings)) {
        setSettings({ ...defaultSettings[category], ...(data.settings as unknown as SettingsMap[T]) });
      }
    } catch (error) {
      logger.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  }, [userId, category]);

  useEffect(() => {
    fetchSettings(true);
  }, [fetchSettings]);

  const saveSettings = async (newSettings: SettingsMap[T]) => {
    if (!user) {
      toast.error("Debes iniciar sesión para guardar la configuración");
      return false;
    }

    setSaving(true);
    try {
      // Check if settings exist
      const { data: existing } = await supabase
        .from("settings")
        .select("id")
        .eq("user_id", user.id)
        .eq("category", category)
        .maybeSingle();

      const settingsJson = newSettings as unknown as Json;

      let error;
      if (existing) {
        // Update existing
        const result = await supabase
          .from("settings")
          .update({ settings: settingsJson })
          .eq("user_id", user.id)
          .eq("category", category);
        error = result.error;
      } else {
        // Insert new
        const result = await supabase
          .from("settings")
          .insert([{
            user_id: user.id,
            category,
            settings: settingsJson,
          }]);
        error = result.error;
      }

      if (error) throw error;

      setSettings(newSettings);
      toast.success("Configuración guardada correctamente");
      return true;
    } catch (error) {
      logger.error("Error saving settings:", error);
      toast.error("Error al guardar la configuración");
      return false;
    } finally {
      setSaving(false);
    }
  };

  return {
    settings,
    setSettings,
    loading,
    saving,
    saveSettings,
    refetch: fetchSettings,
  };
}
